import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { useCallback, useEffect, useMemo } from "react";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { EntradaProduto } from "../../../../domain/models/entrada_produtos_lista";
import {
  EntradaProdutoForm,
  EntradaProdutoMultiSchema,
} from "../../../schemas/Entrada_Produtos/entrada_produtos";
import { DropDownFornecedor } from "../../../../../../common/components/dropdown_fornecedor";
import { DefaultMultiAutocomplete } from "../../../../../../common/components/default_multi_autocomplete";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { GenerateEntradaProdutosMultiColumns } from "../columns/entrada_produtos_multi";
import { FornecedorDropDown } from "../../../../../../common/domain/models/fornecedor_dropdown";
import { GridRowId } from "@mui/x-data-grid";
import { ProdutoServicoDropDown } from "../../../../../../common/domain/models/produto_servico_dropdown";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedEntradaProduto: EntradaProduto | undefined;
  entradaProduto: EntradaProduto[] | undefined;
  produtosServicosDropDown: ProdutoServicoDropDown[];
  centroCustoLista: CentroCustoDropDown[];
  selectedProdutosServicosQuery: ProdutoServicoDropDown[] | null;
  fornecedorLista: FornecedorDropDown[];
  handleProdutosServicosQueryChange: (
    items: ProdutoServicoDropDown[] | null,
  ) => void;
  onClose: () => void;
  onSubmit: (e: EntradaProdutoForm) => void;
  onUpdate: () => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
  OnActionCentroCustoChange(
    idCentroCusto: number | null,
    idEntradaProduto: number,
  ): void;
  OnActionValorChange(valor: string | null, idLinhaGrid: GridRowId): void;
  OnActionCentroCustoChange(
    idCentroCusto: number | null,
    idLinhaGrid: GridRowId,
  ): void;
  OnActionQuantidadeChange(
    quantidade: number | null,
    idLinhaGrid: GridRowId,
  ): void;
  OnActionFornecedorChange(
    idFornecedor: number | null,
    idLinhaGrid: GridRowId,
  ): void;
  onNewProdutoServico: () => void;
};

export const EntradaProdutoMultiModal = ({
  open,
  saving,
  loading,
  entradaProduto,
  fornecedorLista,
  selectedEntradaProduto,
  centroCustoLista,
  produtosServicosDropDown,
  selectedProdutosServicosQuery,
  handleProdutosServicosQueryChange,
  OnActionCentroCustoChange,
  OnActionQuantidadeChange,
  OnActionFornecedorChange,
  OnActionValorChange,
  onNewProdutoServico,
  onClose,
  onSubmit,
  onOpenChangeHistory,
  onUpdate,
}: Props) => {
  const hookForm = useForm<EntradaProdutoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(EntradaProdutoMultiSchema),
  });

  useEffect(() => {
    if (entradaProduto && entradaProduto.length === 0) {
      hookForm.reset({ Data: new Date() });
    }
  }, [hookForm, entradaProduto]);

  const handleChangeHistory = useCallback(() => {
    if (selectedEntradaProduto) {
      onOpenChangeHistory(
        selectedEntradaProduto.IdEntradaProduto,
        LogTipoTabela.ENTRADA_PRODUTO,
      );
    }
  }, [selectedEntradaProduto, onOpenChangeHistory]);

  const columnsDef = useMemo(() => {
    return GenerateEntradaProdutosMultiColumns({
      OnActionValorChange: OnActionValorChange,
      centroCustoLista: centroCustoLista,
      OnActionCentroCustoChange: OnActionCentroCustoChange,
      OnActionQuantidadeChange: OnActionQuantidadeChange,
      fornecedorLista: fornecedorLista,
      OnActionFornecedorChange: OnActionFornecedorChange,
    });
  }, [
    OnActionCentroCustoChange,
    OnActionFornecedorChange,
    OnActionQuantidadeChange,
    OnActionValorChange,
    centroCustoLista,
    fornecedorLista,
  ]);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{
        component: 'form',
        onSubmit: hookForm.handleSubmit(onSubmit)
      }}
      // <-- FORÇA o modal a usar 98% da largura total do monitor
      sx={{ "& .MuiDialog-paper": { maxWidth: "none", width: "98vw", margin: "16px" } }}
    >
      <DialogTitle>Entrada múltiplas de produtos</DialogTitle>

      <DialogContent>
        <FormContainer title={"Cadastrar/Editar"} arrangement="fullRow">
          <div className="flex flex-col gap-y-4 w-full pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormDatePickerInput
                name="Data"
                label="Data"
                reactHookForm={hookForm}
                required={true}
              />
              <DefaultMultiAutocomplete
                label="Produto"
                options={produtosServicosDropDown}
                getOptionKey={(x) => x.idProdutoServico}
                getOptionLabel={(x) => x.descricao}
                value={selectedProdutosServicosQuery}
                required={true}
                onChange={handleProdutosServicosQueryChange}
                onNew={onNewProdutoServico}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DropDownFornecedor
                name="IdFornecedor"
                label="Fornecedor"
                reactHookForm={hookForm}
              />
              <DefaultFormAutocomplete
                name="IdCentroCusto"
                label="Centro de Custo"
                required={true}
                newButtonLabel="Novo"
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                options={centroCustoLista}
                reactHookForm={hookForm}
                onNew={() => { }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DefaultFormInput
                name="Quantidade"
                label="Quantidade"
                reactHookForm={hookForm}
              />
              <DefaultFormInput
                name="NrNotaFiscal"
                label="N° Nota Fiscal"
                reactHookForm={hookForm}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormMoneyMaskedInput
                name="ValorUnitario"
                label="Valor Unitário (R$)"
                largeNumbers={true}
                reactHookForm={hookForm}
              />
              <FormMoneyMaskedInput
                name="ValorTotal"
                label="Valor Total(R$)"
                largeNumbers={true}
                readOnly={true}
                reactHookForm={hookForm}
              />
            </div>

            <div className="w-[120px]">
              <PrimaryButton type="submit" buttonColor="primary-green" disabled={saving}>
                {saving ? "Incluindo..." : "Incluir"}
              </PrimaryButton>
            </div>
          </div>
        </FormContainer>

        <div style={{ height: 450, width: "100%", marginTop: "24px" }}>
          <StyledDataGrid
            tableName="Entrada_Produtos_multi_lista"
            disableColumnMenu={false}
            showToolbar={true}
            disableRowSelectionOnClick={true}
            getRowId={(row: EntradaProduto) => row.IdEntradaProduto}
            columns={columnsDef}
            textSize="medio"
            autoHeight={true}
            getRowHeight={() => "auto"}
            // loading={viewModel.loading}
            checkboxSelection={false}
            rows={entradaProduto ?? []}
            pagination={false}
          />
        </div>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {isNotNullOrUndefined(selectedEntradaProduto) && (
              <PrimaryButton buttonColor="yellow" onClick={handleChangeHistory}>
                <span className="text-xs">Historico de alterações</span>
              </PrimaryButton>
            )}
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={onClose}
              >
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  onClick={() => onUpdate()}
                  buttonColor="primary-green"
                >
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
